import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Sparkles, Sun, Scale } from "lucide-react";
import { TriLanguageBlock } from "@/components/i18n/TriLanguageBlock";
import { storyToTriItem, sadhanaStepToTriItem } from "@/lib/i18n/tri-language";
import { sanitizeAcharyaAnswer } from "@/lib/acharya/sanitizeAcharyaAnswer";

/** Story cards, tattva, sādhana, dharma — shared by web + test UI */
export function AcharyaStructuredBlocks({ data, token, anonymous = false }) {
  if (!data) return null;
  const safe = sanitizeAcharyaAnswer(data);
  const storyCards = safe.story_cards ?? [];
  const tattvaHighlights = safe.tattva_highlights ?? [];
  const dharmaPrinciples = (safe.dharma_principles ?? []).filter(
    (p) => p && String(p).toLowerCase() !== "null"
  );
  const sourcesUsed = safe.shastra_grounding?.sources_used ?? [];
  const showSadhana = !anonymous && Boolean(safe.sadhana_recommendation?.trim());

  return (
    <div className="mt-4 space-y-3 pl-2 border-l-2 border-primary/20" data-testid="acharya-structured-blocks">
      {tattvaHighlights.length > 0 && (
        <Card className="bg-primary/5 border-none shadow-none">
          <CardHeader className="py-2 px-4">
            <CardTitle className="text-xs flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Tattva highlights
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 text-xs space-y-1">
            {tattvaHighlights.map((t, idx) => (
              <p key={`${t}-${idx}`}>{t}</p>
            ))}
          </CardContent>
        </Card>
      )}

      {storyCards.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-2">
          {storyCards.map((s, idx) => (
            <Card key={s?.id ?? `story-${idx}`} className="bg-amber-50/50 shadow-none">
              <CardHeader className="py-2 px-3">
                <CardTitle className="text-xs flex items-center gap-1">
                  <BookOpen className="w-3 h-3" /> {s?.title ?? "Story"}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3 text-xs text-muted-foreground">
                <Badge variant="outline" className="mb-1 text-[10px]">
                  {s?.source === "purana" ? "Purāṇa" : s?.source ?? "śāstra"}
                </Badge>
                <TriLanguageBlock
                  item={storyToTriItem({
                    canonical: { summary_en: s?.summary ?? "", short_story: s?.summary ?? "" },
                    ...s,
                  })}
                  meaningPrefix="summary"
                  meaningAudioMode="story"
                  compact
                  token={token}
                  className="text-muted-foreground text-xs"
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showSadhana && (
        <Card className="bg-green-50/40 shadow-none">
          <CardContent className="py-3 px-4 text-xs flex gap-2">
            <Sun className="w-4 h-4 shrink-0 text-amber-600" />
            <div>
              <p className="font-medium">Sādhana</p>
              <TriLanguageBlock
                item={sadhanaStepToTriItem(safe.sadhana_recommendation)}
                meaningPrefix="meaning"
                meaningAudioMode="sadhana"
                compact
                token={token}
                className="text-muted-foreground"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {dharmaPrinciples.length > 0 && (
        <div className="flex flex-wrap gap-1 items-center">
          <Scale className="w-3 h-3 text-muted-foreground" />
          {dharmaPrinciples.map((p, idx) => (
            <Badge key={`${p}-${idx}`} variant="secondary" className="text-[10px] capitalize">
              {String(p).replace(/_/g, " ")}
            </Badge>
          ))}
        </div>
      )}

      {sourcesUsed.length > 0 && (
        <p className="text-[10px] text-muted-foreground">
          Śāstra grounding (summaries): {sourcesUsed.join(", ")}
        </p>
      )}

      {safe.personalized_guidance?.trim() && (
        <p className="text-xs text-muted-foreground italic">{safe.personalized_guidance}</p>
      )}
    </div>
  );
}
